import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, X, Image as ImageIcon, Palette, Package, GripVertical, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { title: "", description: "", image_url: "", price: "", price_label: "", category: "product", sort_order: 0, published: true };

export default function AdminShowroom() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // id or "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/showroom?admin=true").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm({ ...EMPTY, sort_order: items.length }); };
  const openEdit = (it) => { setEditing(it.id); setForm({ ...it, price: it.price ?? "" }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur téléversement");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: form.price === "" ? null : Number(form.price) };
      if (editing === "new") await api.post("/showroom", payload);
      else await api.put(`/showroom/${editing}`, payload);
      toast.success("Fiche enregistrée");
      cancel(); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette fiche ?")) return;
    try {
      await api.delete(`/showroom/${id}`);
      toast.success("Supprimé"); load();
    } catch { toast.error("Erreur suppression"); }
  };

  const togglePub = async (it) => {
    try {
      await api.put(`/showroom/${it.id}`, { ...it, published: !it.published });
      load();
    } catch { toast.error("Erreur"); }
  };

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (target) => {
    if (dragIdx === null || dragIdx === target) return;
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(target, 0, moved);
    setItems(next);
    setDragIdx(null);
    try {
      await api.post("/showroom/bulk-sort", { order: next.map((x) => x.id) });
    } catch { toast.error("Erreur ordre"); load(); }
  };

  return (
    <div data-testid="admin-showroom" className="space-y-8">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-light">Showroom</h1>
          <p className="text-white/50 text-sm mt-1">Produits dérivés et œuvres d'art exposés physiquement.</p>
        </div>
        <Button onClick={openNew} data-testid="showroom-add" className="bg-[#C8A75A] text-black hover:bg-white uppercase tracking-widest text-xs">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle fiche
        </Button>
      </header>

      {editing && (
        <div data-testid="showroom-editor" className="bg-[#1E1E1E] border border-[#C8A75A]/40 rounded-md p-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A]">{editing === "new" ? "Nouvelle fiche" : "Modifier la fiche"}</p>
            <button onClick={cancel} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="grid md:grid-cols-[220px_1fr] gap-6">
            {/* Preview + upload */}
            <div>
              <div className="aspect-[3/4] rounded-md overflow-hidden bg-[#0D0D0D] border border-white/10 mb-3">
                {form.image_url ? <img src={form.image_url} alt="preview" className="w-full h-full object-cover" /> : (
                  <div className="w-full h-full flex items-center justify-center text-white/30 text-xs uppercase tracking-widest"><ImageIcon className="w-6 h-6 mr-2" /> Aucune photo</div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="showroom-file" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="w-full border-[#C8A75A]/40 text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black text-xs uppercase tracking-widest">
                <Upload className="w-4 h-4 mr-2" /> {uploading ? "Envoi…" : "Photo HD"}
              </Button>
              <Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="…ou coller une URL" className="bg-[#0D0D0D] border-white/10 text-white text-xs mt-2 h-9" />
            </div>

            <div className="space-y-3">
              <Row label="Titre *"><Input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="showroom-title" className="bg-[#0D0D0D] border-white/10 text-white h-11" /></Row>
              <div className="grid md:grid-cols-2 gap-3">
                <Row label="Catégorie">
                  <Select value={form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger className="bg-[#0D0D0D] border-white/10 text-white h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                      <SelectItem value="product">Produit dérivé</SelectItem>
                      <SelectItem value="art">Œuvre d'art</SelectItem>
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Prix (€)"><Input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" /></Row>
              </div>
              <Row label="Prix (alternative texte)"><Input value={form.price_label} onChange={(e) => set("price_label", e.target.value)} placeholder="Sur demande" className="bg-[#0D0D0D] border-white/10 text-white h-11" /></Row>
              <Row label="Description courte"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white" /></Row>
              <div className="flex items-center gap-4 pt-2">
                <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                  <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} /> Publié sur le site
                </label>
                <Button onClick={save} disabled={saving} data-testid="showroom-save" className="ml-auto bg-[#C8A75A] text-black hover:bg-white uppercase tracking-widest text-xs">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "…" : "Enregistrer"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it, idx) => (
          <div
            key={it.id}
            draggable
            onDragStart={() => onDragStart(idx)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(idx)}
            data-testid={`showroom-item-${it.id}`}
            className={`bg-[#1E1E1E] border rounded-md overflow-hidden ${it.published ? "border-white/10" : "border-white/5 opacity-60"}`}
          >
            <div className="relative aspect-[4/3] bg-[#0D0D0D]">
              {it.image_url ? <img src={it.image_url} alt={it.title} className="w-full h-full object-cover" /> : (
                <div className="w-full h-full flex items-center justify-center text-white/20">{it.category === "art" ? <Palette className="w-8 h-8" /> : <Package className="w-8 h-8" />}</div>
              )}
              <span className="absolute top-2 left-2 bg-black/60 backdrop-blur text-[9px] uppercase tracking-widest text-[#C8A75A] px-2 py-1 rounded-sm border border-[#C8A75A]/30">
                {it.category === "art" ? "Œuvre" : "Produit"}
              </span>
              <GripVertical className="absolute top-2 right-2 w-4 h-4 text-white/40 cursor-grab" />
            </div>
            <div className="p-4">
              <p className="font-display text-lg text-white leading-tight">{it.title}</p>
              {it.description && <p className="text-white/50 text-xs mt-1 line-clamp-2">{it.description}</p>}
              <p className="text-[#C8A75A] text-sm mt-3">
                {it.price ? new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(it.price) : (it.price_label || "—")}
              </p>
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/5">
                <button onClick={() => openEdit(it)} className="text-xs text-white/60 hover:text-[#C8A75A] uppercase tracking-widest">Éditer</button>
                <button onClick={() => togglePub(it)} title={it.published ? "Dépublier" : "Publier"} className="ml-auto text-white/50 hover:text-[#C8A75A]">
                  {it.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => remove(it.id)} className="text-white/50 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!items.length && !editing && (
        <div className="bg-[#1E1E1E] border border-dashed border-white/10 rounded-md p-16 text-center text-white/40">
          <p className="uppercase tracking-widest text-xs mb-2">Aucune fiche</p>
          <p className="text-sm">Cliquez sur <em>Nouvelle fiche</em> pour ajouter votre premier produit ou œuvre d'art.</p>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
